import chartTheme from "@components/Charts/chartTheme";
import TitledChartSkeleton from "@components/TitledChartSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import { LineChart } from "@dive.tech/ui-graphs";
import LineChartTooltip from "@dive.tech/ui-graphs/Tooltips/LineChartTooltip";
import { Box, Typography } from "@mui/material";
import { LineSeries, PointTooltipProps } from "@nivo/line";
import { useModelMediaResponseCurve } from "@services/api";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface ModelResponseCurveByChannelProps {
  mediaName: string;
  mediaId: number;
  color: string;
}
export const ModelResponseCurveByChannel = ({
  mediaName,
  mediaId,
  color
}: ModelResponseCurveByChannelProps) => {
  const { currentClient } = useContext(ClientContext);
  const { t } = useTranslation();
  // Ref and state for dynamic width
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState<number>(1000);

  const { data, isFetching: isLoading } = useModelMediaResponseCurve(
    mediaId,
    currentClient.model.id
  );

  const { chartData, avgMedia } = useMemo(() => {
    if (data) {
      return {
        chartData: [
          {
            id: data.name,
            color: color,
            data: data.points.map((point) => {
              return {
                x: parseInt(parseFloat(point.x).toFixed(0)),
                y: point.y
              };
            })
          }
        ],
        avgMedia: data.avg_media
      };
    }
    return { chartData: [], avgMedia: 0 };
  }, [data, color]);

  // Measure container width on mount and resize
  useEffect(() => {
    const updateWidth = () => {
      if (chartContainerRef.current) {
        const width = chartContainerRef.current.offsetWidth;
        setChartWidth(width > 0 ? width : 1000);
      }
    };

    updateWidth();

    // Use ResizeObserver for better performance
    const resizeObserver = new ResizeObserver(updateWidth);
    if (chartContainerRef.current) {
      resizeObserver.observe(chartContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <Box
      className="w-full pt-4"
      sx={{ height: "100%" }}
      key={`${mediaId}-response-curve`}
      ref={chartContainerRef}
    >
      {isLoading ? (
        <TitledChartSkeleton />
      ) : (
        <Box className="w-full pt-4 pl-6" sx={{ height: "100%" }}>
          <Typography variant="h6" component="div" className="pl-4">
            {mediaName}
          </Typography>
          <Box className="w-full" sx={{ height: "500px" }}>
            <LineChart
              data={chartData}
              responsiveLineProps={{
                tooltip: ({ point }: PointTooltipProps<LineSeries>) =>
                  LineChartTooltip({
                    point,
                    withSerieId: false,
                    xAxis: { xType: "number", xDecimals: 0 },
                    yAxis: { yType: "number", yDecimals: 0 }
                  }),
                theme: chartTheme,
                width: chartWidth,
                height: 500,
                yScale: {
                  type: "linear",
                  min: "auto"
                },
                xScale: {
                  type: "linear",
                  min: "auto"
                },
                axisLeft: {
                  tickSize: 10,
                  legend: currentClient.client.kpi_label,
                  legendOffset: -60,
                  legendPosition: "middle"
                },
                axisBottom: {
                  tickRotation: -45,
                  legend: t("mmmodel.response_curves.media_spend_legend", {
                    currency: currentClient.client.currency
                  }),
                  legendOffset: 75,
                  legendPosition: "middle"
                },
                margin: { top: 20, left: 70, right: 50, bottom: 120 },
                legends: [],
                markers: [
                  {
                    axis: "x",
                    value: avgMedia,
                    lineStyle: { stroke: "#000000", strokeWidth: 1 },
                    legend: `${t("mmmodel.response_curves.avg_media_legend")}: ${avgMedia.toFixed(0)}`
                  }
                ]
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};
